import { useEffect, useState } from 'react';
import styles from './BookingManagementBookingTable.module.css';
import commonStyles from "components/commonStyle/commonStyle.module.css";
import { FaCircleMinus } from "react-icons/fa6";
import BookingListTable from 'components/bookingListTable/BookingListTable';
import { getParentKids } from 'services/get-parents-kids';
import { BookingList } from 'modules/booking';
import { BookingListSortConfig } from 'utils/bookingList';
import { getBlockData } from 'services/workTime/get-block-data';
import { getParentsKidBooking } from 'services/booking/get-parents-kid-bookings';
import { KidsData } from 'modules/profile';
import CustomerCancelPopup from '../customerBookinkPooup/costumerCancelPopup';
import CustomerBookingPopup from '../customerBookinkPooup/customerBookingPopup';
import { FaHistory } from 'react-icons/fa';



export default function BookingManagementBookingTable() {
    const [bookingsData, setBookingsData] = useState<BookingList[]>([]);
    const [showFutureBooking, setShowFutureBooking] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState(false);
    const fetchData = async () => {
        setIsLoading(true);

        try {
            const kidsDataRes = await getParentKids();

            if (kidsDataRes.kids && kidsDataRes.kids.length > 0) {
                const kidIds = kidsDataRes.kids.map((kid: KidsData) => kid.id);

                if (kidIds.length > 0) {
                    const kidsBookingsData = await getParentsKidBooking(
                        kidIds,
                        undefined,
                        ['confirmed', 'cancelledPending', 'cancelledRejected', 'cancelledConfirmed'],
                        showFutureBooking
                    );

                    if (kidsBookingsData.success) {
                        const completedBookingsData = await Promise.all(
                            kidsBookingsData.Kids_bookings_data.map(async (booking: any) => {
                                const kid = kidsDataRes.kids.find((kid: KidsData) => kid.id === booking.kid_id);
                                const block = await getBlockData(booking.work_time_slot_id);

                                const hasCancelledPending = ['cancelledPending'].includes(booking.recurrence_date_0_status ?? '') ||
                                                            ['cancelledPending'].includes(booking.recurrence_date_1_status ?? '') ||
                                                            ['cancelledPending'].includes(booking.recurrence_date_2_status ?? '') ||
                                                            ['cancelledPending'].includes(booking.recurrence_date_3_status ?? '');

                                let status = '';
                                if (booking.status === 'confirmed') {
                                    status = hasCancelledPending ? 'teilweise offen' : 'teilweise storniert';
                                } else if (booking.status === 'cancelledPending') {
                                    status = 'offen';
                                } else if (booking.status === 'cancelledConfirmed') {
                                    status = 'bestätigt';
                                } else {
                                    status = 'abgelehnt';
                                }

                                return {
                                    ...booking,
                                    name: `${kid?.first_name} ${kid?.last_name}`,
                                    age: kid?.age,
                                    status,
                                    block: `${booking.start_time.substring(0, 5) || ''} - ${booking.end_time.substring(0, 5) || ''}`,
                                    request_date: booking.request_date?.substring(0, 10) || ''
                                };
                            })
                        );

                        const filteredBookingsData = completedBookingsData.filter((booking: BookingList) => {
                            if (booking.recurrence === 'serial' && booking.status === 'teilweise storniert') {
                                return (
                                    ['cancelledPending', 'cancelledConfirmed'].includes(booking.recurrence_date_0_status ?? '') ||
                                    ['cancelledPending', 'cancelledConfirmed'].includes(booking.recurrence_date_1_status ?? '') ||
                                    ['cancelledPending', 'cancelledConfirmed'].includes(booking.recurrence_date_2_status ?? '') ||
                                    ['cancelledPending', 'cancelledConfirmed'].includes(booking.recurrence_date_3_status ?? '')
                                );
                            } else if (booking.recurrence === 'onetime' && booking.status === 'teilweise storniert') {
                                return false;
                            }
                            return true;
                        });

                        filteredBookingsData.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

                        setBookingsData(filteredBookingsData);
                    } else {
                        console.warn('Fehler beim Abrufen der daten:', kidsBookingsData.message);
                    }
                }
            }
        } catch (error) {
            console.error('Fehler beim Abrufen der daten:', error);
        } finally {
            setIsLoading(false);
        }
    };



    useEffect(() => {
        fetchData();
    }, [showFutureBooking]);

    const [sortConfig, setSortConfig] = useState<BookingListSortConfig>({ key: null, direction: 'asc' });

    const sortedBookings = [...bookingsData].sort((a, b) => {
        if (sortConfig.key) {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];
            if (aValue === undefined) return 1;
            if (bValue === undefined) return -1;

            if (
                (sortConfig.key === 'request_date' || sortConfig.key === 'date') &&
                typeof aValue === 'string' &&
                typeof bValue === 'string'
            ) {
                const aDate = new Date(aValue.split('.').reverse().join('-'));
                const bDate = new Date(bValue.split('.').reverse().join('-'));

                if (aDate < bDate) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aDate > bDate) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
            } else {
                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
            }
        }
        return 0;
    });

    const handleSort = (key: 'name' | 'age' | 'request_date' | 'date' | 'status') => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const [isBookingPopupOpen, setIsBookingPopupOpen] = useState(false);
    const handleCloseBookingPopup = () => setIsBookingPopupOpen(false);
    const [isCancelPopupOpen, setIsCancelPopupOpen] = useState(false);
    const handleOpenCancelPopup = () => { fetchData(); setIsCancelPopupOpen(true); }
    const handleCloseCancelPopup = () => { fetchData(); setIsCancelPopupOpen(false); }

    return (
        <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
                <h2>Buchungsanfragen</h2>

                <button className={`${commonStyles.commonButton} ${styles.cancelButton}`} onClick={handleOpenCancelPopup}>
                    <p>Stornierung</p>
                    <FaCircleMinus className={commonStyles.commonButtonIcon} />
                </button>
            </div>
            <BookingListTable bookings={sortedBookings} onSort={handleSort} sortConfig={sortConfig} isCancel={true} onClose={() => { fetchData(); }} />
            {showFutureBooking &&
                <button className={`${commonStyles.commonButton} ${styles.bookingButton} ${styles.showAllButton}`}
                    onClick={() => setShowFutureBooking(false)}
                >
                    {isLoading ? (
                        <div className={commonStyles.spinner}></div>
                    ) : (
                        "Vorherige Stornierungen anzeigen"
                    )}

                    <FaHistory className={commonStyles.commonButtonIcon} />
                </button>}
            <CustomerBookingPopup isOpen={isBookingPopupOpen} onClose={handleCloseBookingPopup} />
            <CustomerCancelPopup isOpen={isCancelPopupOpen} onClose={handleCloseCancelPopup} />

        </div>
    );
}
